import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart, formatTL } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

const FALLBACK = "https://images.unsplash.com/photo-1643822308521-1da534425d82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const isDealer = user && user.role === "dealer";

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => { setP(r.data); setActiveImg(r.data.image_url || (r.data.gallery && r.data.gallery[0]) || null); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="wb-container py-32 text-center">
        <h1 className="wb-h2">Ürün bulunamadı</h1>
        <Link to="/urunler" className="wb-btn-secondary mt-8 inline-flex">Tüm ürünler</Link>
      </div>
    );
  }
  if (!p) return <div className="wb-container py-32 text-[#a0aabf]">Yükleniyor...</div>;

  const gallery = [p.image_url, ...(p.gallery || [])].filter(Boolean);
  const hasPrice = Number(p.price) > 0;
  const displayPrice = isDealer && p.dealer_price != null ? p.dealer_price : p.price;

  const handleAdd = () => {
    add(p, qty);
    toast.success(`${p.name} (${qty} adet) sepete eklendi`);
  };

  return (
    <>
      <SEO
        title={p.seo_title || p.name}
        description={p.seo_description || p.short_description}
        image={p.image_url}
        type="product"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product",
          name: p.name, description: p.short_description, image: p.image_url,
          brand: { "@type": "Brand", name: "Wünberg" },
          ...(hasPrice ? { offers: { "@type": "Offer", price: p.price, priceCurrency: "TRY" } } : {}),
        }}
      />
      <section className="pt-28 pb-12">
        <div className="wb-container">
          <Link to="/urunler" className="text-sm text-[#a0aabf] hover:text-[#d4af37] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Ürünlere dön
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8">
            <div>
              <div className="aspect-square bg-[#0c1f44] overflow-hidden">
                <img src={activeImg || FALLBACK} alt={p.name} className="w-full h-full object-cover" />
              </div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-3 mt-3">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setActiveImg(g)} data-testid={`gallery-thumb-${i}`}
                      className={`aspect-square overflow-hidden border ${activeImg === g ? "border-[#d4af37]" : "border-white/10"}`}>
                      <img src={g} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="wb-overline">{p.category}</div>
              <h1 className="wb-h2 mt-3" data-testid="product-name">{p.name}</h1>
              <p className="wb-body mt-5">{p.short_description}</p>

              <div className="mt-7 pb-6 border-b border-white/10">
                {hasPrice ? (
                  <>
                    {isDealer && p.dealer_price != null && (
                      <div className="text-sm text-[#a0aabf] line-through">{formatTL(p.price)}</div>
                    )}
                    <div className="font-serif text-4xl text-[#d4af37]">{formatTL(displayPrice)}</div>
                    <div className="text-xs uppercase tracking-widest text-[#a0aabf] mt-2">
                      {p.volume} · KDV %20 dahildir
                      {isDealer && p.dealer_price != null && <span className="text-[#d4af37] ml-2">· Bayi indirimi %{p.dealer_discount_percent}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-serif text-2xl">Fiyat için iletişime geçin</div>
                    <div className="text-xs uppercase tracking-widest text-[#a0aabf] mt-2">{p.volume}</div>
                  </>
                )}
              </div>

              {hasPrice ? (
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex items-center border border-white/15">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-white/5" data-testid="qty-dec"><Minus size={14} /></button>
                    <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1")))} className="w-14 bg-transparent text-center outline-none" data-testid="qty-input" />
                    <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-white/5" data-testid="qty-inc"><Plus size={14} /></button>
                  </div>
                  <button onClick={handleAdd} className="wb-btn-primary flex-1" data-testid="product-add-to-cart">
                    <ShoppingBag size={16} /> Sepete Ekle
                  </button>
                </div>
              ) : (
                <Link to="/iletisim" className="wb-btn-primary inline-flex mt-6" data-testid="product-cta-contact">Fiyat / Bilgi Al</Link>
              )}

              {p.features?.length > 0 && (
                <div className="mt-10">
                  <div className="wb-overline mb-4">Özellikler</div>
                  <ul className="space-y-2.5">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" /><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.colors?.length > 0 && (
                <div className="mt-10">
                  <div className="wb-overline mb-4">Renk Seçenekleri ({p.colors.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {p.colors.map((c, i) => (
                      <div key={i} title={c} className="w-8 h-8 border border-white/20" style={{ background: c }} />
                    ))}
                  </div>
                </div>
              )}

              {isDealer && (
                <div className="mt-10 bg-[#d4af37]/5 border border-[#d4af37]/20 p-4 text-xs text-[#a0aabf]">
                  <strong className="text-[#d4af37]">Bayi notu:</strong> Bayi siparişlerinde kargo alıcıya aittir.
                </div>
              )}
            </div>
          </div>

          {p.description && (
            <div className="mt-20 max-w-3xl">
              <div className="wb-overline">Detaylı Açıklama</div>
              <div className="wb-body mt-4 whitespace-pre-line">{p.description}</div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
